import React, { useState, useEffect } from 'react';
import { getServicesByCategory, getAttributesForCategory } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SendMessageModal from '../components/SendMessageModal';
import './Company.css';

function Company() {
  const [companyServices, setCompanyServices] = useState([]);
  const [attributeNames, setAttributeNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Handle service selection button click
  const handleServiceSelection = (serviceId, serviceTitle, serviceType) => {
    // If user is not logged in, redirect to sign-in with return URL
    if (!currentUser) {
      const redirectUrl = `/service-purchase?service=${serviceId}&type=${serviceType}`;
      navigate(`/signin?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }
    
    // If user is logged in but not verified
    if (currentUser && !currentUser.isVerified) {
      navigate('/verify-email');
      return;
    }
    
    // If user is logged in and verified, proceed to purchase page
    navigate(`/service-purchase?service=${serviceId}&type=${serviceType}`);
  };

  // Handle Book a Call button click
  const handleBookCall = (service) => {
    setSelectedService(service);
    setShowModal(true);
  };

  // Close modal handler
  const handleCloseModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    // Track page view with GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page_title: 'Company Formation Services - rkube',
      page_location: window.location.href,
      page_path: window.location.pathname
    });

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch company services
        const servicesData = await getServicesByCategory('Company');
        setCompanyServices(servicesData);
        
        // Fetch attribute names for Company category
        const attributesData = await getAttributesForCategory('Company');
        setAttributeNames(attributesData.attribute_names || []);
        
        setLoading(false);
        setRetrying(false);
      } catch (error) {
        console.error('Error fetching company data:', error);
        setError('Failed to load company services. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [retrying]); // Re-run when retrying changes

  // Helper function to get cell value
  const getCellValue = (service, attributeName) => {
    const value = service.attributes?.[attributeName];
    return value || 'N/A';
  };

  // Get the service type for button text
  const getServiceTypeLabel = (service) => {
    if (service.title.includes('LLC')) return 'LLC';
    if (service.title.includes('Corporation')) return 'Corporation';
    if (service.title.includes('Non-Profit')) return '501(c)(3)';
    if (service.title.includes('Dissolution')) return 'Dissolution';
    return 'Service';
  };

  // Check if price is custom/variable (not a fixed price)
  const isCustomPricing = (price) => {
    // Check if price contains "fees", "custom", "varies", etc.
    return /fees|custom|varies|call|quote/i.test(price);
  };

  const handleRetry = () => {
    setRetrying(true);
  };

  return (
    <div className="service-page-container">
      <div className="services-page-hero enhanced">
        <div className="container">
          <h1>Form, scale, and manage with confidence</h1>
          <p className="services-page-description">
            From incorporation to day-to-day ops, we automate your entity's back office. 
            Get access to smart mailroom services, automated filings, and structured support 
            throughout your company's lifecycle.
          </p>
          
          <div className="services-intro">
            <p>Whether you're starting a new business or managing an existing one, our comprehensive entity services are designed to keep you compliant and focused on growth.</p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="service-page-content">
          {/* Service features cards section */}
          <section className="services-features-grid">
            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon green">
                  <span className="icon">📋</span>
                </div>
                <h3>Business Formation</h3>
              </div>
              <p>Start your business with confidence. Our formation services handle all the paperwork, state filings, and legal requirements to get your entity up and running smoothly.</p>
            </div>

            <div className="services-card hide-card">
              <div className="services-card-header">
                <div className="services-card-icon blue">
                  <span className="icon">📮</span>
                </div>
                <h3>Virtual Mailroom</h3>
              </div>
              <p>Get a professional business address with our virtual mailroom services. We'll receive, scan, and forward your important mail and keep your business address consistent.</p>
            </div>

            <div className="services-card hide-card">
              <div className="services-card-header">
                <div className="services-card-icon purple">
                  <span className="icon">📑</span>
                </div>
                <h3>Document Management</h3>
              </div>
              <p>Keep all your important corporate documents organized and accessible in one secure location with our digital document management system.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon gold">
                  <span className="icon">✅</span>
                </div>
                <h3>Ongoing Compliance</h3>
              </div>
              <p>Stay compliant with state and federal regulations with our automated compliance monitoring, alerts, and filing services to help avoid costly penalties.</p>
            </div>
          </section>

          <section className="company-services">
            <h2>Our Services</h2>
            {loading ? (
              <p>Loading services...</p>
            ) : error ? (
              <div className="error-container">
                <p className="error-message">{error}</p>
                <button className="retry-button" onClick={handleRetry}>Retry</button>
              </div>
            ) : companyServices.length === 0 ? (
              <p>No company services available at this time.</p>
            ) : (
              <>
                {/* Desktop table view */}
                <div className="packages-table-container">
                  <table className="packages-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        {companyServices.filter(service => !service.title.includes('Dissolution')).map(service => (
                          <th key={service.id}>{service.title}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="price-row">
                        <td className="feature-name">Price</td>
                        {companyServices.filter(service => !service.title.includes('Dissolution')).map(service => (
                          <td key={`${service.id}-price`} className="price-cell">{service.price}</td>
                        ))}
                      </tr>
                      {attributeNames.map(attrName => (
                        <tr key={attrName}>
                          <td className="feature-name">{attrName}</td>
                          {companyServices.filter(service => !service.title.includes('Dissolution')).map(service => (
                            <td key={`${service.id}-${attrName}`}>
                              {getCellValue(service, attrName)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      
                      <tr className="subscribe-row">
                        <td></td>
                        {companyServices.filter(service => !service.title.includes('Dissolution')).map(service => (
                          <td key={`${service.id}-action`}>
                            {isCustomPricing(service.price) ? (
                              <button 
                                className="subscribe-button"
                                onClick={() => handleServiceSelection(service.id, service.title, getServiceTypeLabel(service))}
                              >
                                {service.title.includes('Dissolution') 
                                  ? `Start Dissolution` 
                                  : `Start my ${getServiceTypeLabel(service)}`}
                              </button>
                            ) : (
                              <button 
                                className="contact-button"
                                onClick={() => handleBookCall(service)}
                              >
                                Book a Call
                              </button>
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  <p className="service-footnote">* State filing fees are not included and will be calculated based on your formation state.</p>
                </div>

                {/* Mobile cards view */}
                <div className="mobile-packages">
                  {companyServices.filter(service => !service.title.includes('Dissolution')).map(service => (
                    <div key={service.id} className="mobile-package-card">
                      <div className="mobile-package-header">
                        {service.title}
                      </div>
                      <table className="mobile-package-features">
                        <tbody>
                          {attributeNames.map(attrName => (
                            <tr key={`mobile-${service.id}-${attrName}`}>
                              <td>{attrName}</td>
                              <td>{getCellValue(service, attrName)}</td>
                            </tr>
                          ))}
                          
                          {/* Price row */}
                          <tr className="mobile-package-price-row">
                            <td>Price</td>
                            <td className="mobile-price-value">{service.price}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="mobile-package-footer">
                        {isCustomPricing(service.price) ? (
                          <button 
                            className="subscribe-button"
                            onClick={() => handleServiceSelection(service.id, service.title, getServiceTypeLabel(service))}
                          >
                            {service.title.includes('Dissolution') 
                              ? `Start Dissolution` 
                              : `Start my ${getServiceTypeLabel(service)}`}
                          </button>
                        ) : (
                          <button 
                            className="contact-button"
                            onClick={() => handleBookCall(service)}
                          >
                            Book a Call
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="service-footnote">* State filing fees are not included and will be calculated based on your formation state.</p>
                </div>

                {/* Dissolution Services Section */}
                <section className="dissolution-services">
                  <h2>Business Dissolution Services</h2>
                  <p className="dissolution-description">Need to dissolve your business entity? Our dissolution services handle all the necessary paperwork and filings to properly close your business in compliance with state regulations.</p>
                  <div className="dissolution-container">
                    {loading ? (
                      <p>Loading services...</p>
                    ) : error ? (
                      <p className="error-message">{error}</p>
                    ) : (
                      <>
                        {companyServices.filter(service => service.title.includes('Dissolution')).map(service => (
                          <div key={service.id} className="dissolution-card">
                            <div className="dissolution-card-header">
                              <h3>{service.title}</h3>
                              <div className="dissolution-price">{service.price}</div>
                            </div>
                            <p className="dissolution-description">Complete dissolution service to formally close your business entity with all required state filings and documentation.</p>
                            <div className="attributes-container">
                              <h4>Service Details:</h4>
                              <ul className="dissolution-features">
                                {attributeNames.map(attrName => (
                                  <li key={`dissolution-${service.id}-${attrName}`}>
                                    <span className="feature-label">{attrName}:</span> {getCellValue(service, attrName)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {isCustomPricing(service.price) ? (
                              <button 
                                className="subscribe-button dissolution-button"
                                onClick={() => handleServiceSelection(service.id, service.title, getServiceTypeLabel(service))}
                              >
                                Start Dissolution
                              </button>
                            ) : (
                              <button 
                                className="contact-button dissolution-button"
                                onClick={() => handleBookCall(service)}
                              >
                                Book a Call
                              </button>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </section>
              </>
            )}
          </section>

          <section className="why-choose-us">
            <h2>Why Choose Us</h2>
            <p>We make forming and managing your business simple with complete support throughout your company's lifecycle.</p>
            
            <div className="benefits-grid">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <span>⏱️</span>
                </div>
                <h3>Fast Processing</h3>
                <p>Get your business formed quickly with our streamlined formation process and expedited filing options.</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <span>🛡️</span>
                </div>
                <h3>Liability Protection</h3>
                <p>Protect your personal assets by properly establishing your business entity with the right structure.</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <span>📱</span>
                </div>
                <h3>Digital Dashboard</h3>
                <p>Manage all your business documents, compliance requirements, and notifications in one convenient platform.</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <span>👥</span>
                </div>
                <h3>Expert Support</h3>
                <p>Get guidance from our team of formation specialists who can answer your questions throughout the process.</p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Send Message Modal */}
      {showModal && (
        <SendMessageModal
          formName={selectedService?.title || 'Company Services'}
          onClose={handleCloseModal}
          showDatePicker={true}
        />
      )}
    </div>
  );
}

export default Company; 